import React, { createContext, useContext, useState, useCallback, useRef, useMemo } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import { toast } from 'react-toastify';
import {
  CREDITS_DISBURSED_IDS,
  CREDIT_DOCUMENTS,
  DOWNLOAD_DOCUMENT,
  PAYROLL_DOCS_FOR_DOWNLOAD,
} from '../../../presentation/containers/credits/creditsList/queries';
import {
  base64ToArrayBuffer,
  buildAllCreditDownloadDocuments,
  getCreditDocumentDisplayName,
  getCreditDocumentDownloadRoute,
  getUniqueCreditDocuments,
} from '../../utils/index';

type TDisbursedDownloadContext = {
  downloadingAllDisbursed: boolean;
  downloadProgress: { current: number; total: number };
  handleDownloadDisbursedDocuments: (order?: string[][]) => void;
};

const DisbursedDownloadContext = createContext<TDisbursedDownloadContext>({
  downloadingAllDisbursed: false,
  downloadProgress: { current: 0, total: 0 },
  handleDownloadDisbursedDocuments: () => {},
});

export const useDisbursedDownload = () => useContext(DisbursedDownloadContext);

export const DisbursedDownloadProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [downloadingAllDisbursed, setDownloadingAllDisbursed] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState({ current: 0, total: 0 });
  const isDownloadingRef = useRef(false);

  const [getDisbursedIds] = useLazyQuery(CREDITS_DISBURSED_IDS, { fetchPolicy: 'network-only' });
  const [getCreditDocuments] = useLazyQuery(CREDIT_DOCUMENTS, { fetchPolicy: 'network-only' });
  const [getPayrollDocs] = useLazyQuery(PAYROLL_DOCS_FOR_DOWNLOAD, { fetchPolicy: 'network-only' });
  const [downloadDocument] = useMutation(DOWNLOAD_DOCUMENT);

  const handleDownloadDisbursedDocuments = useCallback(
    async (order?: string[][]) => {
      if (isDownloadingRef.current) return;
      isDownloadingRef.current = true;
      setDownloadingAllDisbursed(true);
      setDownloadProgress({ current: 0, total: 0 });

      try {
        if (!('showDirectoryPicker' in window)) {
          toast('Tu navegador no soporta la selección de carpetas. Usa Chrome o Edge.', {
            autoClose: 4000,
            type: 'warning',
            hideProgressBar: true,
          });
          setDownloadingAllDisbursed(false);
          isDownloadingRef.current = false;
          return;
        }

        // eslint-disable-next-line no-undef
        let directoryHandle: any = null;
        try {
          directoryHandle = await (window as any).showDirectoryPicker({
            mode: 'readwrite',
            startIn: 'downloads',
          });
        } catch (e: any) {
          if (e?.name === 'AbortError') {
            setDownloadingAllDisbursed(false);
            isDownloadingRef.current = false;
            return;
          }
          toast('Error al seleccionar la carpeta.', {
            autoClose: 3000,
            type: 'error',
            hideProgressBar: true,
          });
          setDownloadingAllDisbursed(false);
          isDownloadingRef.current = false;
          return;
        }

        const { data: disbursedData } = await getDisbursedIds({
          variables: { ...(order?.length ? { order } : {}) },
        });
        const credits = disbursedData?.creditsDisbursedIds || [];

        if (!credits.length) {
          toast('No se encontraron créditos desembolsados.', {
            autoClose: 3000,
            type: 'info',
            hideProgressBar: true,
          });
          setDownloadingAllDisbursed(false);
          isDownloadingRef.current = false;
          return;
        }

        const creditosFolder = await directoryHandle.getDirectoryHandle('creditos', {
          create: true,
        });

        let totalDownloaded = 0;
        let totalCredits = 0;

        setDownloadProgress({ current: 0, total: credits.length });

        // eslint-disable-next-line no-restricted-syntax
        for (const credit of credits) {
          try {
            // Obtener documentos del crédito y documentos personales en paralelo
            // eslint-disable-next-line no-await-in-loop
            const [{ data: docsData }, { data: payrollData }] = await Promise.all([
              getCreditDocuments({ variables: { cId: credit.cId } }),
              getPayrollDocs({ variables: { cId: credit.cId } }),
            ]);

            const docs = buildAllCreditDownloadDocuments({
              creditDocuments: docsData?.creditDocuments || [],
              creditReports: docsData?.creditReports || [],
              debtCapacityInfo: docsData?.getCreditDiscountCapacityInformationByCreditId,
              payrollData: payrollData?.getCreditPayrollCompanyById,
              documentsToRequest: payrollData?.getCreditPayrollDocumentToRequest || [],
              cId: credit.cId,
            });

            if (docs.length) {
              const uniqueDocs = getUniqueCreditDocuments(docs);

              const folderName = String(credit.cConsecutive || credit.cId);
              // eslint-disable-next-line no-await-in-loop
              const creditFolder = await creditosFolder.getDirectoryHandle(folderName, {
                create: true,
              });

              totalCredits += 1;

              // Descargar documentos del crédito, notificaciones, capacidad de descuento y documentos personales.
              // eslint-disable-next-line no-restricted-syntax
              for (const doc of uniqueDocs) {
                const route = getCreditDocumentDownloadRoute(doc, credit.cId);
                const downloadName = getCreditDocumentDisplayName(doc);
                let targetFolder = creditFolder;

                if (doc?.downloadFolder) {
                  try {
                    // eslint-disable-next-line no-await-in-loop
                    targetFolder = await creditFolder.getDirectoryHandle(doc.downloadFolder, {
                      create: true,
                    });
                  } catch {
                    targetFolder = creditFolder;
                  }
                }

                try {
                  // eslint-disable-next-line no-await-in-loop
                  const result = await downloadDocument({
                    variables: { input: { name: downloadName, route } },
                  });
                  const b64 = result?.data?.downloadDocuments?.base64;
                  if (b64) {
                    // eslint-disable-next-line no-await-in-loop
                    const arrayBuffer = base64ToArrayBuffer(b64);
                    if (arrayBuffer instanceof Uint8Array) {
                      const blob = new Blob([arrayBuffer], { type: 'application/octet-stream' });
                      // eslint-disable-next-line no-await-in-loop
                      const fileHandle = await targetFolder.getFileHandle(downloadName, {
                        create: true,
                      });
                      // eslint-disable-next-line no-await-in-loop
                      const writable = await fileHandle.createWritable();
                      // eslint-disable-next-line no-await-in-loop
                      await writable.write(blob);
                      // eslint-disable-next-line no-await-in-loop
                      await writable.close();
                      totalDownloaded += 1;
                    }
                  }
                } catch {
                  // Continuar con el siguiente documento
                }
              }
            }
          } catch {
            // Continuar con el siguiente crédito
          }

          setDownloadProgress((prev) => ({ ...prev, current: prev.current + 1 }));
        }

        toast(
          `Descarga completa: ${totalDownloaded} documento(s) de ${totalCredits} crédito(s) guardados.`,
          {
            autoClose: 5000,
            type: 'success',
            hideProgressBar: true,
          }
        );
      } catch {
        toast('Error al descargar los documentos de créditos desembolsados.', {
          autoClose: 3000,
          type: 'error',
          hideProgressBar: true,
        });
      }
      setDownloadingAllDisbursed(false);
      isDownloadingRef.current = false;
    },
    [getDisbursedIds, getCreditDocuments, getPayrollDocs, downloadDocument]
  );

  const contextValue = useMemo(
    () => ({
      downloadingAllDisbursed,
      downloadProgress,
      handleDownloadDisbursedDocuments,
    }),
    [downloadingAllDisbursed, downloadProgress, handleDownloadDisbursedDocuments]
  );

  return (
    <DisbursedDownloadContext.Provider value={contextValue}>
      {children}
    </DisbursedDownloadContext.Provider>
  );
};
